import { uiPaper } from "shared/ui/uiSurface"
import {
  Close,
  EditOutlined,
  MessageOutlined,
  PhoneAndroidOutlined,
  SendOutlined,
  DirectionsCarOutlined,
} from "@mui/icons-material"
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CourierItem,
  CourierVehicleInfo,
  useGetCourierDevicesQuery,
  useSendCourierPushMessageMutation,
  useSendCourierRemoteCommandMutation,
  useToggleCourierMutation,
} from "../../apis/appApi"
import { getAvatarColor } from "../customers/utils"

dayjs.extend(relativeTime)

type Props = {
  courier: CourierItem | null
  open: boolean
  onClose: () => void
  onEdit: (courier: CourierItem) => void
  onToggle: (courier: CourierItem) => void
}

const InfoRow: FC<{ label: string; value: React.ReactNode; "data-testid"?: string }> = ({
  label,
  value,
  "data-testid": testId,
}) => (
  <Stack direction="row" spacing={1} data-testid={testId}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
      {label}:
    </Typography>
    <Typography variant="body2">{value ?? "\u2014"}</Typography>
  </Stack>
)

function formatDate(raw: string | number | null | undefined): string {
  if (!raw) return "\u2014"
  return dayjs(raw).format("MMM D, YYYY")
}

function formatDateTime(raw: string | number | null | undefined): string {
  if (!raw) return "\u2014"
  return dayjs(raw).format("MMM D, YYYY HH:mm")
}

function parseSelectedProviders(appSettings: string | null): string[] {
  if (!appSettings) return []
  try {
    const parsed = JSON.parse(appSettings)
    if (Array.isArray(parsed?.selectedProviders)) {
      return parsed.selectedProviders.map(String)
    }
    return []
  } catch {
    return []
  }
}

function parseVehicleInfo(mobileData: string | null): CourierVehicleInfo | null {
  if (!mobileData) return null
  try {
    const parsed = JSON.parse(mobileData)
    return parsed?.vehicleInfo ?? null
  } catch {
    return null
  }
}

const VEHICLE_TYPE_LABELS: Record<string, string> = {
  car: "couriers.vehicle.car",
  van: "couriers.vehicle.van",
  truck: "couriers.vehicle.truck",
  motorcycle: "couriers.vehicle.motorcycle",
  bicycle: "couriers.vehicle.bicycle",
  walking: "couriers.vehicle.walking",
}

export const CourierDetailDrawer: FC<Props> = ({ courier, open, onClose, onEdit, onToggle }) => {
  const { t } = useTranslation()
  const [toggleCourier] = useToggleCourierMutation()
  const [toggleConfirmOpen, setToggleConfirmOpen] = useState(false)
  const [messageDialogOpen, setMessageDialogOpen] = useState(false)
  const [commandDialogOpen, setCommandDialogOpen] = useState(false)
  const [message, setMessage] = useState("")
  const [selectedCommand, setSelectedCommand] = useState("REFRESH_ROUTE")
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  })

  // Fetch devices when drawer is open (F110)
  const { data: devicesData, isLoading: devicesLoading } = useGetCourierDevicesQuery(courier?.id ?? 0, {
    skip: !courier || !open,
  })
  const devices = devicesData?.data ?? []

  const [sendPushMessage, { isLoading: isSending }] = useSendCourierPushMessageMutation()
  const [sendRemoteCommand, { isLoading: isCommandSending }] = useSendCourierRemoteCommandMutation()

  const handleToggle = useCallback(async () => {
    if (!courier) return
    try {
      await toggleCourier(courier.id).unwrap()
      setToggleConfirmOpen(false)
      onToggle(courier)
    } catch (error) {
      console.error("Failed to toggle courier:", error)
    }
  }, [courier, toggleCourier, onToggle])

  const handleSendMessage = useCallback(async () => {
    if (!courier || !message.trim()) return
    try {
      const result = await sendPushMessage({ id: courier.id, message: message.trim() }).unwrap()
      setMessageDialogOpen(false)
      setMessage("")
      setSnackbar({
        open: true,
        message: t("couriers.pushMessage.success", { count: result.deviceCount }),
        severity: "success",
      })
    } catch {
      setSnackbar({ open: true, message: t("couriers.pushMessage.error"), severity: "error" })
    }
  }, [courier, message, sendPushMessage, t])

  const handleSendCommand = useCallback(async () => {
    if (!courier) return
    try {
      const result = await sendRemoteCommand({ id: courier.id, command: selectedCommand }).unwrap()
      setCommandDialogOpen(false)
      setSnackbar({
        open: true,
        message: t("couriers.remoteCommand.success", { count: result.deviceCount }),
        severity: "success",
      })
    } catch {
      setSnackbar({ open: true, message: t("couriers.remoteCommand.error"), severity: "error" })
    }
  }, [courier, selectedCommand, sendRemoteCommand, t])

  const fullName = courier ? `${courier.firstName} ${courier.lastName}`.trim() : ""
  const initial = fullName.charAt(0)?.toUpperCase() || "?"
  const selectedProviders = parseSelectedProviders(courier?.appSettings ?? null)
  const vehicleInfo = parseVehicleInfo(courier?.mobileData ?? null)

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        data-testid="courier-detail-drawer"
        slotProps={{
          paper: {
            ...uiPaper("UI-DLV2-122"),
            sx: { width: 550, maxWidth: "100vw", display: "flex", flexDirection: "column" },
          },
        }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
            {courier && (
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  fontSize: 20,
                  fontWeight: 600,
                  bgcolor: getAvatarColor(fullName),
                }}
              >
                {initial}
              </Avatar>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {fullName || t("loading")}
              </Typography>
              {courier?.username && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {courier.username}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" data-testid="courier-drawer-close-btn">
            <Close />
          </IconButton>
        </Stack>

        {courier && (
          <Box sx={{ p: 2, overflow: "auto", flex: 1 }}>
            {/* Courier Info */}
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              {t("couriers.drawer.courierInfo")}
            </Typography>
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              <InfoRow
                label={t("couriers.drawer.phone")}
                value={courier.phoneNumber || "\u2014"}
                data-testid="courier-detail-phone"
              />
              <InfoRow
                label={t("couriers.drawer.email")}
                value={courier.email || "\u2014"}
                data-testid="courier-detail-email"
              />
              <InfoRow
                label={t("couriers.drawer.created")}
                value={formatDate(courier.dateCreated)}
                data-testid="courier-detail-created"
              />
            </Stack>

            {/* Roles */}
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              {t("couriers.drawer.roles")}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: "wrap", gap: 0.5 }}>
              {courier.roles.map((role) => {
                const isManager = role.authority === "ROLE_HAULER_MANAGER"
                return (
                  <Chip
                    key={role.id}
                    label={role.authority.replace("ROLE_", "").replace(/_/g, " ")}
                    size="small"
                    sx={
                      isManager
                        ? { bgcolor: "#e3f2fd", color: "#1565c0" }
                        : undefined
                    }
                    data-testid={`courier-role-${role.authority}`}
                  />
                )
              })}
            </Stack>

            {/* Provider Subscriptions */}
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              {t("couriers.drawer.providerSubscriptions")}
            </Typography>
            <Box sx={{ mb: 3 }}>
              {selectedProviders.length > 0 ? (
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 0.5 }}>
                  {selectedProviders.map((providerId) => (
                    <Chip
                      key={providerId}
                      label={providerId}
                      size="small"
                      variant="outlined"
                      data-testid={`courier-provider-${providerId}`}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("couriers.drawer.noProviderSubscriptions")}
                </Typography>
              )}
            </Box>

            {/* Last Login */}
            <Divider sx={{ mb: 2 }} />
            <InfoRow
              label={t("couriers.drawer.lastLogin")}
              value={courier.lastLogin ? formatDate(courier.lastLogin) : t("couriers.drawer.never")}
              data-testid="courier-detail-last-login"
            />

            {/* Devices (F110) */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              {t("couriers.drawer.devices")}
            </Typography>
            {devicesLoading ? (
              <Stack spacing={1} sx={{ mb: 3 }}>
                <Skeleton variant="rounded" height={60} />
              </Stack>
            ) : devices.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t("couriers.drawer.noDevices")}
              </Typography>
            ) : (
              <Stack spacing={1.5} sx={{ mb: 3 }}>
                {devices.map((device) => (
                  <Box
                    key={device.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "grey.50",
                      border: "1px solid",
                      borderColor: "grey.200",
                    }}
                    data-testid={`courier-device-${device.id}`}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <PhoneAndroidOutlined fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight={600}>
                        {[device.manufacturer, device.model].filter(Boolean).join(" ") || device.platform || "Unknown device"}
                      </Typography>
                      {device.platform && (
                        <Chip label={device.platform} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                      )}
                    </Stack>
                    <Stack spacing={0.5} sx={{ pl: 3.5 }}>
                      {device.osVersion && (
                        <Typography variant="caption" color="text.secondary">
                          {t("couriers.drawer.deviceOs")}: {device.osVersion}
                        </Typography>
                      )}
                      {(device.appUnifiedVersion || device.appNativeVersion) && (
                        <Typography variant="caption" color="text.secondary">
                          {t("couriers.drawer.deviceApp")}: {device.appUnifiedVersion || device.appNativeVersion}
                        </Typography>
                      )}
                      {device.batteryCharge && (
                        <Typography variant="caption" color="text.secondary">
                          {t("couriers.drawer.deviceBattery")}: {device.batteryCharge}
                        </Typography>
                      )}
                      {device.gpsStatus && (
                        <Typography variant="caption" color="text.secondary">
                          {t("couriers.drawer.deviceGps")}: {device.gpsStatus}
                        </Typography>
                      )}
                      {device.networkType && (
                        <Typography variant="caption" color="text.secondary">
                          {t("couriers.drawer.deviceNetwork")}: {device.networkType}
                          {device.carrierName ? ` (${device.carrierName})` : ""}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {t("couriers.drawer.deviceLastSeen")}: {formatDateTime(device.lastUpdated)}
                      </Typography>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}

            {/* Vehicle Info (F113) */}
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <DirectionsCarOutlined fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={600}>
                {t("couriers.drawer.vehicleInfo")}
              </Typography>
            </Stack>
            {vehicleInfo && (vehicleInfo.licensePlate || vehicleInfo.vehicleType) ? (
              <Stack spacing={1.5} sx={{ mb: 3 }}>
                {vehicleInfo.licensePlate && (
                  <InfoRow
                    label={t("couriers.drawer.licensePlate")}
                    value={vehicleInfo.licensePlate}
                    data-testid="courier-detail-license-plate"
                  />
                )}
                {vehicleInfo.vehicleType && (
                  <InfoRow
                    label={t("couriers.drawer.vehicleType")}
                    value={
                      VEHICLE_TYPE_LABELS[vehicleInfo.vehicleType]
                        ? t(VEHICLE_TYPE_LABELS[vehicleInfo.vehicleType])
                        : vehicleInfo.vehicleType
                    }
                    data-testid="courier-detail-vehicle-type"
                  />
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t("couriers.drawer.noVehicleInfo")}
              </Typography>
            )}

            {/* Actions */}
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1.5}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<EditOutlined />}
                data-testid="courier-drawer-edit-btn"
                onClick={() => onEdit(courier)}
              >
                {t("couriers.drawer.edit")}
              </Button>

              {/* Send Message (F111) */}
              <Tooltip title={devices.length === 0 ? t("couriers.pushMessage.noDevices") : ""}>
                <span>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<MessageOutlined />}
                    disabled={devices.length === 0}
                    data-testid="courier-drawer-send-message-btn"
                    onClick={() => {
                      setMessage("")
                      setMessageDialogOpen(true)
                    }}
                  >
                    {t("couriers.drawer.sendMessage")}
                  </Button>
                </span>
              </Tooltip>

              {/* Send Command (F112) */}
              <Tooltip title={devices.length === 0 ? t("couriers.remoteCommand.noDevices") : ""}>
                <span>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<SendOutlined />}
                    disabled={devices.length === 0}
                    data-testid="courier-drawer-send-command-btn"
                    onClick={() => {
                      setSelectedCommand("REFRESH_ROUTE")
                      setCommandDialogOpen(true)
                    }}
                  >
                    {t("couriers.drawer.sendCommand")}
                  </Button>
                </span>
              </Tooltip>

              <Button
                variant="outlined"
                fullWidth
                color={courier.enabled ? "warning" : "success"}
                data-testid="courier-drawer-toggle-btn"
                onClick={() => setToggleConfirmOpen(true)}
              >
                {courier.enabled ? t("couriers.drawer.disable") : t("couriers.drawer.enable")}
              </Button>
            </Stack>
          </Box>
        )}
      </Drawer>

      {/* Toggle confirmation */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-123") }}
        open={toggleConfirmOpen}
        onClose={() => setToggleConfirmOpen(false)}
        maxWidth="xs"
        data-testid="courier-toggle-dialog"
      >
        <DialogTitle>
          {courier?.enabled ? t("couriers.toggle.disableTitle") : t("couriers.toggle.enableTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {courier?.enabled
              ? t("couriers.toggle.disableMessage", { name: fullName })
              : t("couriers.toggle.enableMessage", { name: fullName })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setToggleConfirmOpen(false)}
            data-testid="courier-toggle-cancel-btn"
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleToggle}
            variant="contained"
            color={courier?.enabled ? "warning" : "success"}
            data-testid="courier-toggle-confirm-btn"
          >
            {courier?.enabled ? t("couriers.drawer.disable") : t("couriers.drawer.enable")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Message Dialog (F111) */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-124") }}
        open={messageDialogOpen}
        onClose={() => setMessageDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        data-testid="courier-send-message-dialog"
      >
        <DialogTitle>{t("couriers.pushMessage.title")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            placeholder={t("couriers.pushMessage.placeholder")}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            inputProps={{ maxLength: 500 }}
            sx={{ mt: 1 }}
            data-testid="courier-send-message-input"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            {message.length}/500
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageDialogOpen(false)}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSendMessage}
            variant="contained"
            disabled={!message.trim() || isSending}
            startIcon={isSending ? <CircularProgress size={16} /> : undefined}
            data-testid="courier-send-message-submit-btn"
          >
            {t("couriers.pushMessage.send")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Command Dialog (F112) */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-125") }}
        open={commandDialogOpen}
        onClose={() => setCommandDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        data-testid="courier-send-command-dialog"
      >
        <DialogTitle>{t("couriers.remoteCommand.title")}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>{t("couriers.drawer.sendCommand")}</InputLabel>
            <Select
              value={selectedCommand}
              onChange={(e) => setSelectedCommand(e.target.value)}
              label={t("couriers.drawer.sendCommand")}
              data-testid="courier-send-command-select"
            >
              <MenuItem value="REFRESH_ROUTE">{t("couriers.remoteCommand.refreshRoute")}</MenuItem>
              <MenuItem value="SYNC_POSITION">{t("couriers.remoteCommand.syncPosition")}</MenuItem>
              <MenuItem value="FORCE_UPDATE">{t("couriers.remoteCommand.forceUpdate")}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommandDialogOpen(false)}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSendCommand}
            variant="contained"
            disabled={isCommandSending}
            startIcon={isCommandSending ? <CircularProgress size={16} /> : undefined}
            data-testid="courier-send-command-submit-btn"
          >
            {t("couriers.remoteCommand.send")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success/error feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}
